import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { LandingRenderer } from "../components/LandingRenderer";
import { api } from "../lib/api";

const setMeta = (name, content, prop) => {
  if (!content) return;
  const sel = prop ? `meta[property="${prop}"]` : `meta[name="${name}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (prop) el.setAttribute("property", prop);
    else el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/public/landing")
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => document.getElementById(location.state.scrollTo)?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [location.state]);

  const seo = data?.content?.seo;
  useEffect(() => {
    if (!seo) return;
    if (seo.title) document.title = seo.title;
    setMeta("description", seo.description);
    setMeta("keywords", seo.keywords);
    setMeta(null, seo.title, "og:title");
    setMeta(null, seo.description, "og:description");
    setMeta(null, seo.og_image_url, "og:image");
  }, [seo]);

  useEffect(() => {
    if (!data?.json_ld) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "hz-jsonld";
    el.textContent = JSON.stringify(data.json_ld);
    document.head.appendChild(el);
    return () => el.remove();
  }, [data?.json_ld]);

  if (failed) {
    return (
      <PublicLayout>
        <section className="bg-hz-bg py-24">
          <div className="mx-auto max-w-2xl px-4 text-center">
            <div className="inline-flex rounded-2xl border border-hz-gray/40 bg-white p-4 shadow-sm"><Logo variant="full" className="h-16" /></div>
            <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-hz-navy sm:text-5xl">Horizonte <span className="text-hz-cyan">2026</span></h1>
            <p className="mt-3 text-lg font-bold uppercase tracking-widest text-hz-blue">Liderazgo con Visión Reformada</p>
            <Button data-testid="hero-register-cta" onClick={() => navigate("/registro")} className="mt-8 h-12 bg-hz-cyan px-8 text-base font-bold text-white hover:bg-hz-blue">
              Realizar pre-registro <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      </PublicLayout>
    );
  }

  if (!data) {
    return (
      <PublicLayout>
        <div className="flex min-h-[50vh] items-center justify-center text-sm font-semibold text-hz-ink/60" data-testid="landing-loading">Cargando…</div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout footerContent={data.content?.footer} contact={data.content?.contact}>
      <div data-content-version={data.version} data-content-hash={data.content_hash} data-testid="landing-content">
        <LandingRenderer content={data.content} />
      </div>
    </PublicLayout>
  );
};

export default LandingPage;
